import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiChevronDown, FiShield, 
  FiActivity, FiZap, FiServer, FiMonitor, FiLayers, 
  FiTool, FiClock, FiRefreshCw, FiAlertCircle, FiDatabase, FiHardDrive, FiHeadphones
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const ManagedHosting = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Managed Hosting Services | ManagedSite.net";
    
    const description = "ManagedSite.net provides managed hosting coordinated with website management, security, backups, monitoring and technical support.";
    const title = "Managed Hosting Services | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const whyMatters = [
    {
      title: "Unsupported Hosting",
      text: "Cheap shared hosting often leaves agencies stuck between the host and the website team.",
      icon: FiAlertCircle
    },
    {
      title: "Slow or Unstable Sites",
      text: "Poor hosting resources and misconfiguration can make websites feel broken even when the design is fine.",
      icon: FiZap
    },
    {
      title: "Backup and Recovery Gaps",
      text: "Hosting without coordinated backups and recovery planning increases downtime risk.",
      icon: FiDatabase
    },
    {
      title: "Split Responsibility",
      text: "When hosting and website management are separate, troubleshooting takes longer and clients lose confidence.",
      icon: FiLayers
    }
  ];

  const coreServices = [
    {
      title: "Managed WordPress Hosting Environment",
      description: "Hosting configured for WordPress websites with practical operational support.",
      examples: ["WordPress-ready environment", "PHP and server basics managed", "Staging coordination when needed", "Environment troubleshooting", "Hosting changes documented"],
      icon: FiServer
    },
    {
      title: "Performance and Stability",
      description: "Help keep hosting resources aligned with website needs.",
      examples: ["Caching support", "Resource review", "Slow-site investigation", "Traffic spike review", "Hosting-related performance fixes"],
      icon: FiZap
    },
    {
      title: "Security Coordination",
      description: "Hosting that works with the same security and monitoring approach used for managed websites.",
      examples: ["SSL support", "Hardening coordination", "Malware response coordination", "Access control basics", "Environment isolation where appropriate"],
      icon: FiShield
    },
    {
      title: "Backups and Recovery Support",
      description: "Hosting support coordinated with backup and recovery processes.",
      examples: ["Backup coordination", "Restore assistance", "Hosting-level recovery support", "Staging restore workflows", "Incident coordination"],
      icon: FiDatabase
    },
    {
      title: "Domains, DNS and SSL Support",
      description: "Practical infrastructure help connected to the hosted website.",
      examples: ["Domain connection support", "DNS review", "SSL setup and repair", "Redirect issues", "Nameserver changes when needed"],
      icon: FiLayers
    },
    {
      title: "Agency-Ready Operations",
      description: "Hosting support designed for agencies managing multiple client websites.",
      examples: ["White-label friendly communication", "Coordinated with website care plans", "Clear escalation path", "Portfolio-friendly support model", "No direct solicitation of agency clients"],
      icon: FiMonitor
    }
  ];

  const faqs = pageFaqs['/services/managed-hosting'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] overflow-hidden pt-24 pb-24 lg:pt-40 lg:pb-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-20 items-center">
            <div className="lg:col-span-7 text-center lg:text-left mb-20 lg:mb-0">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Managed Hosting</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10">
                Hosting Managed by the Same Team That Manages the Website
              </h1>
              <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-2xl mx-auto lg:mx-0 font-medium">
                Reliable hosting coordinated with website management, security, backups, and monitoring — so one team owns the environment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/digital-review" className="home-button">
                  Discuss Hosting
                </Link>
                <Link to="/agency-plans" className="home-button-secondary">
                  View Agency Plans
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] bg-[#EAF1EC] border border-[#3B7C6D]/20 shadow-2xl p-10 sm:p-12">
                <div className="absolute top-0 left-0 w-full h-2 bg-[#174C3C]"></div>
                <div className="space-y-6">
                  {[
                    { icon: FiHardDrive, label: "Hosting Environment", status: "Managed" },
                    { icon: FiActivity, label: "Uptime Monitoring", status: "Active" },
                    { icon: FiDatabase, label: "Backups", status: "Coordinated" },
                    { icon: FiShield, label: "Security", status: "Hardened" },
                    { icon: FiLayers, label: "SSL and DNS", status: "Reviewed" },
                    { icon: FiHeadphones, label: "Agency Support", status: "Available" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl flex items-center justify-between border border-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#EAF1EC] p-3 rounded-xl text-[#174C3C]">
                          <SafeIcon icon={item.icon} className="w-6 h-6" />
                        </div>
                        <span className="font-extrabold text-[#17211E]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-3 py-1 rounded-full">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Managed Hosting Matters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Hosting Problems Become Website Problems Quickly</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Hosting is not just server space. It affects speed, uptime, email authentication, SSL, staging, backups, plugin compatibility and how quickly issues can be fixed.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] hover:shadow-md transition-all">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Managed Hosting Includes */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What Managed Hosting Is Designed to Cover</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {coreServices.map((service, i) => (
              <div key={i} className="bg-white p-10 md:p-12 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-xl text-[#17211E]/75 mb-10 leading-relaxed font-medium">{service.description}</p>
                <div className="flex-grow">
                  <div className="text-sm font-bold text-[#3B7C6D] uppercase tracking-wider mb-6">Examples:</div>
                  <ul className="space-y-4">
                    {service.examples.map((item, j) => (
                      <li key={j} className="flex items-start text-[#17211E]/80 text-lg font-medium">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning Section */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Not Cheap Shared Hosting</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              ManagedSite.net is not trying to win on the lowest hosting price. The value is coordinated management, technical ownership and fewer gaps between the website, hosting and support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { title: "One Technical Owner", text: "Hosting issues are handled by the same team responsible for website management and support." },
              { title: "Better Troubleshooting", text: "Fewer handoffs between vendors when something breaks." },
              { title: "Built for Agencies", text: "Hosting can sit behind your brand while ManagedSite.net handles the technical layer." }
            ].map((point, i) => (
              <div key={i} className="bg-white p-10 rounded-3xl border border-[#3B7C6D]/10 text-center">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-4">{point.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agency Hosting Guidance Pricing */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6">Agency Hosting Pricing Guidance</h2>
            <p className="text-xl text-[#17211E]/75 mb-6 font-medium leading-relaxed">
              These are directional agency pricing ranges for planning purposes. Final hosting pricing depends on infrastructure, site complexity and support requirements.
            </p>
            <div className="inline-block bg-[#174C3C] text-white px-6 py-2 rounded-full text-sm font-black uppercase tracking-widest">
              Pricing guidance only. Final rates are confirmed during agency onboarding.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse bg-[#FAF8F3] rounded-3xl overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-[#EAF1EC] border-b border-[#3B7C6D]/10">
                    <th className="py-6 px-8 text-lg font-black text-[#17211E]">Hosting Type</th>
                    <th className="py-6 px-8 text-lg font-black text-[#17211E]">Suggested Agency Price</th>
                  </tr>
                </thead>
                <tbody className="text-lg font-bold">
                  <tr className="border-b border-[#EAF1EC]">
                    <td className="py-6 px-8 text-[#17211E]">Standard managed hosting</td>
                    <td className="py-6 px-8 text-[#174C3C]">$20 to $29 per site / month</td>
                  </tr>
                  <tr className="border-b border-[#EAF1EC]">
                    <td className="py-6 px-8 text-[#17211E]">Business managed hosting</td>
                    <td className="py-6 px-8 text-[#174C3C]">$39 to $49 per site / month</td>
                  </tr>
                  <tr className="border-b border-[#EAF1EC]">
                    <td className="py-6 px-8 text-[#17211E]">WooCommerce hosting</td>
                    <td className="py-6 px-8 text-[#174C3C]">$79+ per site / month</td>
                  </tr>
                  <tr>
                    <td className="py-6 px-8 text-[#17211E]">High-traffic or custom server</td>
                    <td className="py-6 px-8 text-[#174C3C]">Custom</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse bg-[#FAF8F3] rounded-3xl overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-[#EAF1EC] border-b border-[#3B7C6D]/10">
                    <th className="py-6 px-8 text-lg font-black text-[#17211E]">Bundle</th>
                    <th className="py-6 px-8 text-lg font-black text-[#17211E]">Suggested Agency Price</th>
                  </tr>
                </thead>
                <tbody className="text-lg font-bold">
                  <tr className="border-b border-[#EAF1EC]">
                    <td className="py-6 px-8 text-[#17211E]">Essential Care + hosting</td>
                    <td className="py-6 px-8 text-[#174C3C]">Quoted with the care plan</td>
                  </tr>
                  <tr className="border-b border-[#EAF1EC]">
                    <td className="py-6 px-8 text-[#17211E]">Managed Care + hosting</td>
                    <td className="py-6 px-8 text-[#174C3C]">Quoted with the care plan</td>
                  </tr>
                  <tr>
                    <td className="py-6 px-8 text-[#17211E]">Advanced Care + WooCommerce hosting</td>
                    <td className="py-6 px-8 text-[#174C3C]">Quoted with the care plan</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-[#FAF8F3] border border-[#EAF1EC] p-10 rounded-[2rem] text-center">
            <p className="text-lg text-[#17211E]/70 font-bold italic mb-10">
              Website management plans are priced separately and quoted after a review. Hosting ranges above are guidance and may change after that review.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 items-center">
              <Link to="/agency-plans" className="home-button">
                View Agency Plans
              </Link>
              <div className="text-lg font-black text-[#17211E]">
                Agency technical support uses a partner rate
              </div>
            </div>
            <p className="mt-6 text-sm text-[#17211E]/50 font-bold">
              Agency technical support for hosting issues outside included plan or hosting scope is billed at the partner rate.
            </p>
          </div>
        </div>
      </section>

      {/* Best Fit */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Hosting for Different Website Needs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
            {[
              { title: "Standard Business Websites", text: "Informational sites and professional service websites that need stable managed hosting." },
              { title: "Business-Critical Sites", text: "Sites that need stronger resources, closer monitoring and faster response coordination." },
              { title: "WooCommerce and Complex Sites", text: "Stores and integrated websites that need higher-capacity hosting and careful change management." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col text-center">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-8 flex-grow">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#17211E]/50 font-bold italic text-lg">
            Complex or high-traffic websites may require a custom hosting recommendation after review.
          </p>
        </div>
      </section>

      {/* How Hosting Onboarding Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center">How Managed Hosting Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { title: "Review", text: "We review the website, traffic, plugins, email needs, current host and risk factors." },
              { title: "Recommend", text: "We recommend the right hosting level and confirm whether website management should be paired with it." },
              { title: "Migrate or Provision", text: "We set up or migrate the hosting environment with DNS and cutover planning." },
              { title: "Manage", text: "We monitor, support and coordinate hosting with ongoing website management when enrolled." }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agency White-Label Section */}
      <section className="py-32 bg-[#174C3C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Agencies</div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 leading-tight">Reseller-Friendly Hosting Behind Your Brand</h2>
              <p className="text-2xl text-[#FAF8F3]/80 mb-12 font-medium leading-relaxed">
                Offer managed hosting to your clients while ManagedSite.net handles the technical operations. Your agency keeps the client relationship.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-16">
                {["White-label delivery", "Coordinated with website management", "Portfolio support model", "Clear escalation path", "No direct solicitation of agency clients"].map((item, i) => (
                  <div key={i} className="flex items-start text-xl font-bold">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#D7B77A] mr-4 flex-shrink-0 mt-1" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="home-button-on-dark">
                  Discuss Agency Hosting
                </Link>
                <Link to="/agency-plans" className="home-button-on-dark-secondary">
                  View Agency Plans
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative select-none opacity-50 text-center">
              <div className="bg-white/5 rounded-[3rem] p-16 border border-white/10 backdrop-blur-sm">
                <div className="text-[#D7B77A] text-6xl font-black mb-10">HOST</div>
                <div className="h-1.5 w-24 bg-[#D7B77A] mx-auto mb-10"></div>
                <div className="space-y-6">
                  <div className="h-4 bg-white/20 rounded-full w-full"></div>
                  <div className="h-4 bg-white/20 rounded-full w-5/6 mx-auto"></div>
                  <div className="h-4 bg-white/20 rounded-full w-4/6 mx-auto"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Routing Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF8F3] rounded-[2.5rem] p-12 md:p-20 shadow-xl border border-[#EAF1EC] text-center max-w-5xl mx-auto">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Hosting Help for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what is happening with hosting, and we will help work out the next step.
            </p>
            <Link to="/digital-review" className="home-button">
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16">Related Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {[
              { label: "Website Management", link: "/services/website-management" },
              { label: "Technical Support", link: "/services/technical-support" },
              { label: "Business Email", link: "/services/business-email" },
              { label: "Agency Plans", link: "/agency-plans" },
              { label: "Technology Consulting", link: "/services/technology-consulting" }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-white p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all">
                {item.label}
              </Link>
            ))}
          </div>
          <Link to="/services" className="home-button-secondary">
            Explore All Services
          </Link>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-white border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#FAF8F3] border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-white transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`} 
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Put Hosting and Website Management Under One Technical Team
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            Reduce vendor gaps and get more dependable hosting backed by real technical ownership.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button-on-dark">
              Discuss Hosting
            </Link>
            <Link to="/agency-plans" className="home-button-on-dark-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManagedHosting;